/**
 * Admin shell: 248px rail (56px compact under 1024px), 48px top bar with breadcrumbs, clock, API
 * status and session cluster, scrolling content. Command palette on ⌘K for navigation.
 */
import { Avatar, Button, CommandPalette, DropdownMenu, LeftRail, StatusDot, TopBar, TopBarContext, fmtClock, type CommandGroup, type RailGroup } from '@agency/ui';
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { LogOut, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { health } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { NAV_GROUPS, matchNav } from './nav.js';

function useClock(): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  return fmtClock(now, { timeZone: 'UTC', label: 'UTC' });
}

type ApiStatus = 'ok' | 'down' | 'unknown';
function useApiStatus(): { status: ApiStatus; mode: string | null } {
  const [state, setState] = useState<{ status: ApiStatus; mode: string | null }>({ status: 'unknown', mode: null });
  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      try {
        const h = await health();
        if (!cancelled) setState({ status: h.ok ? 'ok' : 'down', mode: h.mode });
      } catch {
        if (!cancelled) setState((s) => ({ status: 'down', mode: s.mode }));
      }
    };
    void probe();
    const t = window.setInterval(() => void probe(), 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, []);
  return state;
}

function useCompact(): boolean {
  const [compact, setCompact] = useState(() => window.matchMedia('(max-width: 1023px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const onChange = () => setCompact(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return compact;
}

export function Shell() {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const clock = useClock();
  const apiStatus = useApiStatus();
  const compact = useCompact();
  const [paletteOpen, setPaletteOpen] = useState(false);

  const match = matchNav(location.pathname);
  const go = (path: string) => void navigate({ to: path });

  const groups = useMemo<RailGroup[]>(
    () =>
      NAV_GROUPS.map((g) => ({
        id: g.id,
        label: g.label,
        items: g.items.map((item) => ({
          id: item.id,
          label: item.label,
          icon: item.icon,
          active: match?.item.id === item.id,
          href: item.path,
          disabled: item.capability ? !session.has(item.capability) : false,
          onSelect: (e) => {
            e.preventDefault();
            go(item.path);
          },
        })),
      })),
    [match, session],
  );

  const commandGroups = useMemo<CommandGroup[]>(
    () => [
      {
        label: 'Navigate',
        items: NAV_GROUPS.flatMap((g) =>
          g.items.map((item) => ({
            id: item.id,
            label: item.label,
            hint: g.label.toUpperCase(),
            icon: item.icon,
            disabled: item.capability ? !session.has(item.capability) : false,
            onSelect: () => go(item.path),
          })),
        ),
      },
      {
        label: 'Session',
        items: [
          { id: 'reauth', label: 'Re-authenticate', icon: RefreshCw, onSelect: () => session.reauthenticate() },
          { id: 'signout', label: 'Sign out', icon: LogOut, onSelect: () => void session.signOut() },
        ],
      },
    ],
    [session],
  );

  const user = session.session?.user;
  const breadcrumbs = [{ label: 'Agency', onSelect: () => go('/') }];
  if (match && match.item.path !== '/') {
    breadcrumbs.push({ label: match.group.label, onSelect: () => go(match.item.path) });
    breadcrumbs.push({ label: match.item.label, onSelect: () => go(match.item.path) });
  } else {
    breadcrumbs.push({ label: 'Overview', onSelect: () => go('/') });
  }
  const detailSegment = detailLabel(location.pathname, match?.item.path);
  if (detailSegment) breadcrumbs.push({ label: detailSegment, onSelect: () => undefined });

  const remaining = session.recentAuthRemainingSeconds;
  const reauthLabel = remaining > 0 ? `Re-auth ${Math.max(1, Math.ceil(remaining / 60))}m` : null;

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-bg text-ink">
      <LeftRail
        collapsed={compact}
        groups={groups}
        onSearch={() => setPaletteOpen(true)}
        onLogoSelect={() => go('/')}
        footer={user ? { name: user.displayName, org: 'Agency', onSelect: () => go(`/people/${user.id}`) } : undefined}
        topSlot={session.session?.mode === 'local' && !compact ? <p className="label-mono px-2 text-brand-text">Local mode</p> : undefined}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          breadcrumbs={breadcrumbs}
          right={
            <>
              <span className="tabular-nums" aria-label="Clock">{clock}</span>
              <StatusDot tone={apiStatus.status === 'ok' ? 'ok' : apiStatus.status === 'down' ? 'brand' : 'dim'} label={apiStatus.status === 'ok' ? 'API OK' : apiStatus.status === 'down' ? 'API down' : 'API --'} pulse={apiStatus.status === 'down'} />
              {reauthLabel ? <TopBarContext>{reauthLabel}</TopBarContext> : <button type="button" onClick={() => session.reauthenticate()} className="uppercase tracking-[0.18em] text-mute hover:text-ink">Re-authenticate</button>}
              {user ? (
                <DropdownMenu
                  align="end"
                  trigger={
                    <button type="button" className="flex items-center gap-2 rounded-sm normal-case tracking-normal" aria-label={`Session: ${user.displayName}`}>
                      <Avatar name={user.displayName} size={24} />
                      <span className="hidden text-xs text-ink xl:inline">{user.displayName}</span>
                    </button>
                  }
                  items={[
                    { type: 'label', id: 'email', label: user.email },
                    { type: 'separator', id: 's1' },
                    { id: 'profile', label: 'My access', onSelect: () => go(`/people/${user.id}`) },
                    { id: 'reauth', label: 'Re-authenticate', icon: RefreshCw, onSelect: () => session.reauthenticate() },
                    { type: 'separator', id: 's2' },
                    { id: 'signout', label: 'Sign out', icon: LogOut, onSelect: () => void session.signOut() },
                  ]}
                />
              ) : null}
            </>
          }
        />
        <main className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} groups={commandGroups} placeholder="Go to" />
      {session.error ? (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2">
          <Button variant="tertiary" icon={RefreshCw} onClick={() => void session.reload()}>Reload session</Button>
        </div>
      ) : null}
    </div>
  );
}

function detailLabel(pathname: string, base: string | undefined): string | null {
  if (!base || base === '/' || pathname === base) return null;
  const rest = pathname.slice(base.length + 1);
  if (!rest) return null;
  const id = rest.split('/')[0] ?? '';
  return id.length > 14 ? `${id.slice(0, 8)}…` : id;
}
